import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Stack,
  Paper,
  Tooltip,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { getApiErrorMessage } from '../utils/apiError';
import PageShell from '../components/PageShell';
import ViewModeToggle from '../components/ViewModeToggle';
import { useLayoutTokens } from '../hooks/useLayoutTokens';
import usePersistentViewMode from '../hooks/usePersistentViewMode';

type TimelineStatus = 'planning' | 'active' | 'paused' | 'completed' | 'cancelled';

interface Timeline {
  id: number;
  user_id: number;
  title: string;
  description?: string;
  start_date: string;
  end_date?: string;
  status: TimelineStatus;
  color: string;
  created_at: string;
  updated_at?: string;
}

interface Milestone {
  id: number;
  timeline_id: number;
  title: string;
  description?: string;
  target_date: string;
  is_completed: boolean;
  completed_date?: string;
  created_at: string;
  updated_at?: string;
}

interface TimelineFormData {
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  status: TimelineStatus;
  color: string;
}

interface MilestoneFormData {
  title: string;
  description: string;
  target_date: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const defaultTimelineForm: TimelineFormData = {
  title: '',
  description: '',
  start_date: new Date().toISOString().slice(0, 16),
  end_date: '',
  status: 'planning',
  color: '#3B82F6',
};

const defaultMilestoneForm: MilestoneFormData = {
  title: '',
  description: '',
  target_date: new Date().toISOString().slice(0, 16),
};

const statusColor: Record<TimelineStatus, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error'> = {
  planning: 'default',
  active: 'primary',
  paused: 'warning',
  completed: 'success',
  cancelled: 'error',
};

const formatDate = (iso?: string | null): string => {
  if (!iso) return 'Open';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Open';
  return d.toLocaleDateString();
};

const Timelines: React.FC = () => {
  const {
    cardContentPaddingSx,
    dialogContentPaddingSx,
    pageGridSpacing,
    pageHeaderSx,
    panelPaddingSx,
    sectionGapSx,
  } = useLayoutTokens();
  const [viewMode, setViewMode, viewModeControls] = usePersistentViewMode('timelines');
  const [timelines, setTimelines] = useState<Timeline[]>([]);
  const [milestonesByTimeline, setMilestonesByTimeline] = useState<Record<number, Milestone[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTimeline, setEditTimeline] = useState<Timeline | null>(null);
  const [timelineForm, setTimelineForm] = useState<TimelineFormData>(defaultTimelineForm);
  const [saveLoading, setSaveLoading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [timelineToDelete, setTimelineToDelete] = useState<Timeline | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [milestoneForm, setMilestoneForm] = useState<MilestoneFormData>(defaultMilestoneForm);
  const [selectedTimelineId, setSelectedTimelineId] = useState<number | null>(null);
  const [milestoneSaveLoading, setMilestoneSaveLoading] = useState(false);

  const fetchTimelines = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/timelines/`);
      setTimelines(response.data);
      setError(null);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load timelines'));
    } finally {
      setLoading(false);
    }
  };

  const fetchMilestones = async (timelineId: number) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/timelines/${timelineId}/milestones`);
      setMilestonesByTimeline((prev) => ({ ...prev, [timelineId]: response.data }));
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load milestones'));
    }
  };

  useEffect(() => {
    fetchTimelines();
  }, []);

  const openCreateDialog = () => {
    setEditTimeline(null);
    setTimelineForm(defaultTimelineForm);
    setDialogOpen(true);
  };

  const openEditDialog = (timeline: Timeline) => {
    setEditTimeline(timeline);
    setTimelineForm({
      title: timeline.title,
      description: timeline.description || '',
      start_date: new Date(timeline.start_date).toISOString().slice(0, 16),
      end_date: timeline.end_date ? new Date(timeline.end_date).toISOString().slice(0, 16) : '',
      status: timeline.status,
      color: timeline.color,
    });
    setDialogOpen(true);
  };

  const saveTimeline = async () => {
    if (!timelineForm.title.trim()) {
      setError('Timeline title is required');
      return;
    }

    try {
      setSaveLoading(true);
      const payload = {
        ...timelineForm,
        start_date: new Date(timelineForm.start_date).toISOString(),
        end_date: timelineForm.end_date ? new Date(timelineForm.end_date).toISOString() : null,
      };

      if (editTimeline) {
        await axios.put(`${API_BASE_URL}/api/timelines/${editTimeline.id}`, payload);
      } else {
        await axios.post(`${API_BASE_URL}/api/timelines/`, payload);
      }
      setDialogOpen(false);
      await fetchTimelines();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to save timeline'));
    } finally {
      setSaveLoading(false);
    }
  };

  const confirmDeleteTimeline = (timeline: Timeline) => {
    setTimelineToDelete(timeline);
    setDeleteDialogOpen(true);
  };

  const deleteTimeline = async () => {
    if (!timelineToDelete) return;

    try {
      setDeleteLoading(true);
      await axios.delete(`${API_BASE_URL}/api/timelines/${timelineToDelete.id}`);
      setDeleteDialogOpen(false);
      setTimelineToDelete(null);
      await fetchTimelines();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to delete timeline'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const openMilestoneDialog = (timelineId: number) => {
    setSelectedTimelineId(timelineId);
    setMilestoneForm(defaultMilestoneForm);
    setMilestoneDialogOpen(true);
  };

  const saveMilestone = async () => {
    if (!selectedTimelineId) return;
    if (!milestoneForm.title.trim()) {
      setError('Milestone title is required');
      return;
    }

    try {
      setMilestoneSaveLoading(true);
      await axios.post(`${API_BASE_URL}/api/timelines/${selectedTimelineId}/milestones`, {
        ...milestoneForm,
        target_date: new Date(milestoneForm.target_date).toISOString(),
        is_completed: false,
      });
      setMilestoneDialogOpen(false);
      await fetchMilestones(selectedTimelineId);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to save milestone'));
    } finally {
      setMilestoneSaveLoading(false);
    }
  };

  const toggleMilestone = async (milestone: Milestone) => {
    try {
      await axios.put(`${API_BASE_URL}/api/timelines/milestones/${milestone.id}`, {
        is_completed: !milestone.is_completed,
        completed_date: !milestone.is_completed ? new Date().toISOString() : null,
      });
      await fetchMilestones(milestone.timeline_id);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to update milestone'));
    }
  };

  const deleteMilestone = async (milestone: Milestone) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/timelines/milestones/${milestone.id}`);
      await fetchMilestones(milestone.timeline_id);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to delete milestone'));
    }
  };

  if (loading) {
    return (
      <PageShell>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Box
        sx={[
          pageHeaderSx,
          {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          },
        ]}
      >
        <Typography variant="h3" component="h1">
          Timelines
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          alignSelf={{ xs: 'stretch', sm: 'center' }}
          justifyContent="flex-end"
        >
          <ViewModeToggle
            value={viewMode}
            onChange={setViewMode}
            ariaLabel="timeline view mode"
            isOverride={viewModeControls.isOverride}
            onUseDefault={viewModeControls.useDefault}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            size="large"
            onClick={openCreateDialog}
          >
            Add Timeline
          </Button>
        </Stack>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={sectionGapSx}>
        Track long-term goals and milestones with real progress states.
      </Typography>

      {error && (
        <Alert severity="error" sx={sectionGapSx} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {timelines.length === 0 ? (
        <Card>
          <CardContent sx={cardContentPaddingSx}>
            <Typography variant="h6" gutterBottom>
              No timelines yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create your first timeline to track long-term projects and goals.
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
              Add Your First Timeline
            </Button>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={pageGridSpacing}>
          {timelines.map((timeline) => {
            const milestones = milestonesByTimeline[timeline.id] || [];
            const completedMilestones = milestones.filter((m) => m.is_completed).length;

            return (
              <Grid item xs={12} md={6} key={timeline.id}>
                <Card sx={{ borderTop: `4px solid ${timeline.color}`, height: '100%' }}>
                  <CardContent sx={cardContentPaddingSx}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {timeline.title}
                      </Typography>
                      <Box>
                        <Button size="small" startIcon={<EditIcon />} onClick={() => openEditDialog(timeline)}>
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => confirmDeleteTimeline(timeline)}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Box>

                    {timeline.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {timeline.description}
                      </Typography>
                    )}

                    <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                      <Chip label={timeline.status} color={statusColor[timeline.status]} size="small" />
                      <Chip
                        label={`${new Date(timeline.start_date).toLocaleDateString()} - ${timeline.end_date ? new Date(timeline.end_date).toLocaleDateString() : 'Open'}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2">Milestones</Typography>
                      <Box display="flex" gap={1}>
                        <Button size="small" onClick={() => fetchMilestones(timeline.id)}>
                          Refresh
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => openMilestoneDialog(timeline.id)}>
                          Add Milestone
                        </Button>
                      </Box>
                    </Box>

                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {completedMilestones}/{milestones.length} completed
                    </Typography>

                    {milestones.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No milestones yet.
                      </Typography>
                    ) : (
                      <Box>
                        {milestones.map((milestone, index) => (
                          <Box key={milestone.id}>
                            {index > 0 && <Divider sx={{ my: 1 }} />}
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Box display="flex" alignItems="center" gap={1}>
                                <Button size="small" onClick={() => toggleMilestone(milestone)}>
                                  {milestone.is_completed ? (
                                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                                  ) : (
                                    <RadioButtonUncheckedIcon fontSize="small" />
                                  )}
                                </Button>
                                <Box>
                                  <Typography variant="body2" sx={{ textDecoration: milestone.is_completed ? 'line-through' : 'none' }}>
                                    {milestone.title}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(milestone.target_date).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              </Box>
                              <Button size="small" color="error" onClick={() => deleteMilestone(milestone)}>
                                Remove
                              </Button>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Stack spacing={1}>
          {timelines.map((timeline) => {
            const milestones = milestonesByTimeline[timeline.id] || [];
            const completedMilestones = milestones.filter((m) => m.is_completed).length;
            const progress = milestones.length
              ? Math.round((completedMilestones / milestones.length) * 100)
              : 0;

            return (
              <Paper
                key={timeline.id}
                sx={[
                  panelPaddingSx,
                  {
                    borderLeft: `5px solid ${timeline.color}`,
                  },
                ]}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'minmax(0, 1.5fr) minmax(150px, 0.8fr) minmax(150px, 0.8fr) auto',
                    },
                    gap: { xs: 1, sm: 2 },
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                      {timeline.title}
                    </Typography>
                    {timeline.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {timeline.description}
                      </Typography>
                    )}
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={timeline.status} color={statusColor[timeline.status]} size="small" />
                    <Chip
                      label={`${formatDate(timeline.start_date)} - ${formatDate(timeline.end_date)}`}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {completedMilestones}/{milestones.length} milestones
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                    />
                  </Box>

                  <Stack direction="row" spacing={0.5} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                    <Tooltip title="Refresh milestones">
                      <IconButton size="small" onClick={() => fetchMilestones(timeline.id)} aria-label={`Refresh milestones for ${timeline.title}`}>
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Add milestone">
                      <IconButton size="small" onClick={() => openMilestoneDialog(timeline.id)} aria-label={`Add milestone to ${timeline.title}`}>
                        <Add fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEditDialog(timeline)} aria-label={`Edit ${timeline.title}`}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => confirmDeleteTimeline(timeline)} aria-label={`Delete ${timeline.title}`}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editTimeline ? 'Edit Timeline' : 'Add Timeline'}</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <TextField
            margin="dense"
            fullWidth
            required
            label="Title"
            value={timelineForm.title}
            onChange={(e) => setTimelineForm((prev) => ({ ...prev, title: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            fullWidth
            multiline
            rows={2}
            label="Description"
            value={timelineForm.description}
            onChange={(e) => setTimelineForm((prev) => ({ ...prev, description: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                value={timelineForm.start_date}
                onChange={(e) => setTimelineForm((prev) => ({ ...prev, start_date: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                value={timelineForm.end_date}
                onChange={(e) => setTimelineForm((prev) => ({ ...prev, end_date: e.target.value }))}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={timelineForm.status}
                  label="Status"
                  onChange={(e) => setTimelineForm((prev) => ({ ...prev, status: e.target.value as TimelineStatus }))}
                >
                  <MenuItem value="planning">Planning</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="paused">Paused</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Color"
                value={timelineForm.color}
                onChange={(e) => setTimelineForm((prev) => ({ ...prev, color: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saveLoading}>Cancel</Button>
          <Button onClick={saveTimeline} variant="contained" disabled={saveLoading}>
            {saveLoading ? <CircularProgress size={20} /> : editTimeline ? 'Save Changes' : 'Add Timeline'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={milestoneDialogOpen} onClose={() => setMilestoneDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Milestone</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <TextField
            margin="dense"
            fullWidth
            required
            label="Title"
            value={milestoneForm.title}
            onChange={(e) => setMilestoneForm((prev) => ({ ...prev, title: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            fullWidth
            multiline
            rows={2}
            label="Description"
            value={milestoneForm.description}
            onChange={(e) => setMilestoneForm((prev) => ({ ...prev, description: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="datetime-local"
            label="Target Date"
            InputLabelProps={{ shrink: true }}
            value={milestoneForm.target_date}
            onChange={(e) => setMilestoneForm((prev) => ({ ...prev, target_date: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMilestoneDialogOpen(false)} disabled={milestoneSaveLoading}>Cancel</Button>
          <Button onClick={saveMilestone} variant="contained" disabled={milestoneSaveLoading}>
            {milestoneSaveLoading ? <CircularProgress size={20} /> : 'Add Milestone'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Timeline</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <Typography>
            Delete "{timelineToDelete?.title}" and all associated milestones?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button onClick={deleteTimeline} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default Timelines;
